/**
 * How the sidebar's 24 top-level entries are grouped into readable sections.
 *
 * The tab tree itself stays in `ADMIN_TABS` — this only says what order the
 * entries appear in and which heading they sit under, so nothing here can
 * change what an admin is allowed to see. Visibility is still decided entirely
 * by the permission check in the nav.
 *
 * ⚠️ Keys must match `ADMIN_TABS` exactly. Anything in ADMIN_TABS that is NOT
 * listed here still renders, under a trailing "More" heading — a tab added to
 * ADMIN_TABS and forgotten here must never silently disappear from the nav.
 * `sectionsForTabs` is what enforces that; there is a unit-style guard in the
 * nav that logs the orphans in development.
 */
export const NAV_SECTIONS = [
  // No heading: these two are the landing pages and read better flush at the
  // top than under a label of their own.
  {id: 'home', label: null, keys: ['Overview', 'Analytics']},
  {
    id: 'customers',
    label: 'Customers',
    keys: ['UsersTab', 'Groups', 'Wallet', 'Subscription'],
  },
  {
    id: 'invest',
    label: 'Invest',
    keys: [
      'AssetManagement',
      'Stocks',
      'Listings',
      'Dividends',
      'Financials',
      'Savings',
    ],
  },
  {
    id: 'support',
    label: 'Support',
    keys: ['AiHelpDesk', 'aiAutomation', 'Support', 'Faq'],
  },
  {
    id: 'engagement',
    label: 'Engagement',
    keys: ['Notification', 'Announcement', 'Management', 'Marketing', 'Topics'],
  },
  {id: 'system', label: 'System', keys: ['Settings', 'Report', 'Plans']},
];

/**
 * Resolves ADMIN_TABS into ordered sections.
 *
 * Returns `[{id, label, entries: [{key, tab}]}]`, dropping sections that end up
 * empty. Any ADMIN_TABS key missing from NAV_SECTIONS is collected into a
 * trailing "More" section rather than being dropped.
 */
export const sectionsForTabs = tabs => {
  const placed = [];
  const sections = NAV_SECTIONS.map(section => {
    const entries = section.keys
      .filter(key => tabs[key])
      .map(key => {
        placed.push(key);
        return {key, tab: tabs[key]};
      });
    return {...section, entries};
  });

  const orphans = Object.keys(tabs).filter(key => placed.indexOf(key) === -1);
  if (orphans.length) {
    sections.push({
      id: 'more',
      label: 'More',
      entries: orphans.map(key => ({key, tab: tabs[key]})),
    });
  }

  return sections.filter(section => section.entries.length > 0);
};

/** The orphan keys, for the development-only warning. */
export const unsectionedKeys = tabs => {
  const known = NAV_SECTIONS.reduce(
    (all, section) => all.concat(section.keys),
    [],
  );
  return Object.keys(tabs).filter(key => known.indexOf(key) === -1);
};
